import { i18next } from '@yola/ws-sdk';
import preloadImage from 'src/js/modules/utils/preload-image';
import optionTypes from '../../constants/option-types';
import computeBorderRadiusFallback from './helpers/compute-border-radius-fallback';
import computeSpacingFallback from './helpers/compute-spacing-fallback';
import createAdjustedEventTraitsGetter from './helpers/create-adjusted-event-traits-getter';
import {
  getCornersAdjustedEventTraits,
  getCornersModifiedEventTraits,
} from './helpers/get-corners-event-traits';
import {
  getSpacingAdjustedEventTraits,
  getSpacingModifiedEventTraits,
} from './helpers/get-spacing-event-traits';
import previewGifs, { columnPreviewGifs } from './constants/preview-gifs';

import ids from './constants/ids';
import settings from './settings';

const EVENT_TRAIT_PREFIX = 'column';

const getThisColumnSettings = () => {
  Object.values(columnPreviewGifs).forEach((gif) => preloadImage(gif));

  return {
    id: ids.THIS_COLUMN,
    priority: 2,
    eventTraitPrefix: EVENT_TRAIT_PREFIX,
    matches(element, config) {
      return element.matches(config.querySelector) && element.closest(config.parentSelector);
    },
    settings: {
      parentSelector: 'ws-block[custom]',
      querySelector: 'ws-column',
    },
    tabs: [
      {
        id: 'appearance',
        accordionTitle: i18next.t('This column'),
        options: [
          {
            id: 'color-scheme',
            type: optionTypes.COLOR_SCHEME,
            title: i18next.t('Color scheme'),
            getAdjustedEventTraits: createAdjustedEventTraitsGetter('Surfaces'),
            popover: {
              content: i18next.t('Change background of this column'),
              imgSrc: previewGifs.COLUMN_BACKGROUND_GIF,
            },
            choices: [
              {
                id: 'ws-light-0',
                value: 'ws-light-0',
              },
              {
                id: 'ws-light-1',
                value: 'ws-light-1',
              },
              {
                id: 'ws-dark-0',
                value: 'ws-dark-0',
              },
              {
                id: 'ws-dark-1',
                value: 'ws-dark-1',
              },
            ],
            attributes: ['surface'],
          },
          {
            id: 'content-alignment',
            type: optionTypes.SECTION,
            title: i18next.t('Content alignment'),
            options: [
              settings.getHorizontalContentAlignment({
                popover: {
                  content: i18next.t('Align the content inside this column horizontally'),
                  imgSrc: previewGifs.COLUMN_HORIZONTAL_CONTENT_ALIGNMENT_GIF,
                },
              }),
              settings.getVerticalContentAlignment({
                popover: {
                  content: i18next.t('Align the content inside this column vertically'),
                  imgSrc: previewGifs.COLUMN_VERTICAL_CONTENT_ALIGNMENT_GIF,
                },
              }),
            ],
          },
          {
            id: 'border',
            type: optionTypes.TOGGLE,
            title: i18next.t('Border'),
            attributes: ['border'],
          },
          {
            id: 'shadow',
            type: optionTypes.TOGGLE,
            title: i18next.t('Shadow'),
            attributes: ['shadow'],
          },
          {
            id: 'corners',
            type: optionTypes.TOGGLEABLE_SECTION,
            title: i18next.t('Corners'),
            getAdjustedEventTraits: getCornersAdjustedEventTraits,
            getModifiedEventTraits: getCornersModifiedEventTraits,
            computeFallbackValue: computeBorderRadiusFallback,
            options: [
              settings.getBorderRadiuses({
                querySelector: 'ws-column',
                getAdjustedEventTraits: getCornersAdjustedEventTraits,
                getModifiedEventTraits: getCornersModifiedEventTraits,
              }),
            ],
          },
          {
            id: 'spacing',
            type: optionTypes.TOGGLEABLE_SECTION,
            title: i18next.t('Spacing'),
            getAdjustedEventTraits: getSpacingAdjustedEventTraits,
            getModifiedEventTraits: getSpacingModifiedEventTraits,
            computeFallbackValue: computeSpacingFallback,
            options: [
              ...settings.getPaddings({
                getAdjustedEventTraits: getSpacingAdjustedEventTraits,
                getModifiedEventTraits: getSpacingModifiedEventTraits,
              }),
            ],
          },
        ],
      },
    ],
  };
};

export default getThisColumnSettings;
